import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import statusService from '../../service/status.service';

const StatusHandler = ({
  children,
  onStatusUpdate,
  showConfirmation = false,
  customStatusFilter = null,
  warningRules = null,
  minimumCommentLength = 10,
  suggestionComments = [],
  customModalHeader = null,
}) => {
  const [statusList, setStatusList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [originalStatus, setOriginalStatus] = useState(null);

  // Récupération des statuts
  const fetchStatuses = useCallback(async () => {
    try {
      const response = await statusService.getAll();
      if (response && response.data) {
        setStatusList(response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching statuses:', error);
      setStatusList([]);
    }
  }, []);

  // Fonction pour obtenir le nom du statut par ID
  const getStatusName = useCallback((statusId) => {
    const status = statusList.find(s => s.id_stat === statusId);
    return status ? status.nom_stat : 'Statut inconnu';
  }, [statusList]);

  // Fonction pour initialiser l'attribution de statut
  const initializeStatusAttribution = useCallback((article) => {
    console.log('🔄 Initialisation attribution statut:', {
      articleId: article.id_art,
      currentStatus: article.id_stat,
      user: 'HiQaody',
      timestamp: new Date().toISOString(),
    });

    fetchStatuses();
    setSelectedArticle(article);
    setOriginalStatus(article.id_stat);
    setSubmitError(null);
    setSubmitSuccess(false);
    setCommentaire('');
    setShowConfirmModal(false);
  }, [fetchStatuses]);

  // Fonction de validation
  const validateSubmission = useCallback(() => {
    if (!selectedArticle?.id_stat) {
      setSubmitError('Veuillez sélectionner un statut');
      return false;
    }

    if (selectedArticle.id_stat === originalStatus) {
      Swal.fire({
        icon: 'info',
        title: 'Aucun changement',
        text: 'Le statut est identique à l\'original. Aucune modification n\'est nécessaire.',
        confirmButtonColor: '#57A863',
      });
      return false;
    }

    if (commentaire.trim().length < minimumCommentLength) {
      setSubmitError(`Le commentaire doit contenir au moins ${minimumCommentLength} caractères`);
      return false;
    }

    return true;
  }, [selectedArticle, originalStatus, commentaire, minimumCommentLength]);

  // Fonction pour obtenir la configuration de confirmation
  const getConfirmationConfig = useCallback((article, statusName) => {
    // Règles d'avertissement par défaut
    const defaultWarningRules = {
      downgradeFromPublishable: {
        condition: (orig, new_stat) => 
          (orig === '3' || orig === 3) && (new_stat === '2' || new_stat === 2),
        config: {
          title: '⚠️ Attention: Action sensible',
          text: `Vous êtes sur le point de rétrograder l'article vers le statut "${statusName}". Cette action est inhabituelle et pourrait nécessiter une justification.`,
          icon: 'warning',
          confirmButtonText: 'Oui, je comprends les implications'
        }
      },
      downgradeFromRejected: {
        condition: (orig, new_stat) => 
          (orig === '7' || orig === 7) && (new_stat === '2' || new_stat === 2),
        config: {
          title: '⚠️ Attention: Action sensible',
          text: `Vous êtes sur le point de changer un article "refusé" vers "${statusName}". Cette action nécessitera une confirmation supplémentaire.`,
          icon: 'warning',
          confirmButtonText: 'Oui, je comprends les implications'
        }
      },
      toPublish: {
        condition: (orig, new_stat) => new_stat === '3' || new_stat === 3,
        config: {
          title: '🚀 Passage en publiable',
          text: `Vous êtes sur le point de marquer l'article comme publiable. Cela signifie qu'il a passé avec succès l'évaluation.`,
          icon: 'success'
        }
      },
      toReject: {
        condition: (orig, new_stat) => new_stat === '7' || new_stat === 7,
        config: {
          title: '❌ Refus d\'article',
          text: `Vous êtes sur le point de refuser l'article. Cette action informera l'auteur que son article ne répond pas aux critères de publication.`,
          icon: 'warning'
        }
      }
    };

    const rules = warningRules || defaultWarningRules;
    
    // Vérifier chaque règle
    for (const rule of Object.values(rules)) {
      if (rule.condition && rule.condition(originalStatus, selectedArticle.id_stat)) {
        return {
          ...rule.config,
          showCancelButton: true,
          confirmButtonColor: '#57A863',
          cancelButtonColor: '#d33',
          cancelButtonText: 'Annuler'
        };
      }
    }

    // Configuration par défaut
    return {
      title: 'Confirmer le changement de statut',
      text: `Vous êtes sur le point de changer le statut vers "${statusName}"`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, changer le statut',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#57A863',
      cancelButtonColor: '#d33',
    };
  }, [originalStatus, selectedArticle, warningRules]);

  // Fonction de confirmation avant soumission
  const handleConfirmSubmit = useCallback(async () => {
    if (!validateSubmission()) return;

    const statusName = getStatusName(selectedArticle.id_stat);

    if (showConfirmation) {
      const confirmConfig = getConfirmationConfig(selectedArticle, statusName);
      const result = await Swal.fire(confirmConfig);
      if (!result.isConfirmed) return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    console.log('📋 Soumission changement statut:', {
      articleId: selectedArticle.id_art,
      fromStatus: originalStatus,
      toStatus: selectedArticle.id_stat,
      commentLength: commentaire.length,
      user: 'HiQaody',
      timestamp: new Date().toISOString(),
    });

    try {
      const submissionData = {
        id_stat: selectedArticle.id_stat,
        content_comm: commentaire,
      };

      await statusService.updateStatus(selectedArticle.id_art, submissionData);
      
      setSubmitSuccess(true);
      
      // Callback de succès
      if (onStatusUpdate) {
        await onStatusUpdate({
          article: selectedArticle,
          newStatus: selectedArticle.id_stat,
          comment: commentaire
        });
      }

      // Notification de succès
      await Swal.fire({
        title: 'Succès!',
        text: 'Le statut de l\'article a été mis à jour avec succès.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });

      // Réinitialiser
      setCommentaire('');
      setShowConfirmModal(false);

      console.log('✅ Changement statut réussi:', {
        articleId: selectedArticle.id_art,
        newStatus: selectedArticle.id_stat,
        user: 'HiQaody',
        timestamp: new Date().toISOString(),
      });

    } catch (err) {
      console.error('❌ Erreur lors du changement de statut:', err);
      
      const errorMessage = err.message || 'Une erreur s\'est produite lors du changement de statut.';
      setSubmitError(errorMessage);

      await Swal.fire({
        title: 'Erreur!',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateSubmission,
    getStatusName,
    showConfirmation,
    getConfirmationConfig,
    selectedArticle,
    originalStatus,
    commentaire,
    onStatusUpdate
  ]);

  // Fonction principale d'attribution de statut
  const handleSubmitStatus = useCallback(async () => {
    if (showConfirmModal) {
      setShowConfirmModal(true);
    } else {
      await handleConfirmSubmit();
    }
  }, [showConfirmModal, handleConfirmSubmit]);

  // Filtrage des statuts
  const filteredStatusList = useMemo(() => {
    if (!statusList.length) return [];
    
    if (customStatusFilter) {
      return statusList.filter(customStatusFilter);
    }
    
    // Filtre par défaut
    return statusList.filter(status => status.id_stat !== 5 && status.id_stat !== 4);
  }, [statusList, customStatusFilter]);

  // Suggestions de commentaires par défaut avec sélection aléatoire limitée à 3
  const defaultSuggestions = [
    'Article nécessite des révisions mineures',
    'Méthodologie à clarifier',
    'Résultats bien présentés mais conclusions à revoir',
    'Références bibliographiques insuffisantes',
    'Structure générale satisfaisante',
    'Données expérimentales convaincantes',
    'Analyse statistique appropriée',
    'Discussion approfondie nécessaire',
    'Originalité du travail remarquable',
    'Clarification des objectifs requise'
  ];

  // Fonction pour mélanger et limiter les suggestions à 3 éléments
  const getRandomSuggestions = useCallback((suggestionsArray, limit = 3) => {
    const shuffled = [...suggestionsArray].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, limit);
  }, []);

  const suggestions = useMemo(() => {
    const baseSuggestions = suggestionComments.length > 0 ? suggestionComments : defaultSuggestions;
    return getRandomSuggestions(baseSuggestions, 3);
  }, [suggestionComments, defaultSuggestions, getRandomSuggestions]);

  // Composant de sélection de statut
  const StatusSelector = useMemo(() => (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-900">
          Nouveau statut
        </label>
        <div className="relative">
          <select
            name="id_stat"
            id="id_stat"
            value={selectedArticle?.id_stat || ''}
            onChange={(e) =>
              setSelectedArticle({
                ...selectedArticle,
                id_stat: e.target.value,
              })
            }
            className="block w-full rounded-lg border-gray-300 bg-white py-3 pl-3 pr-10 text-sm shadow-sm transition-all duration-200 focus:border-[#57A863] focus:ring-2 focus:ring-[#57A863]/20"
          >
            <option value="" className="text-gray-500">
              Sélectionner un nouveau statut
            </option>
            {filteredStatusList.map((status) => (
              <option
                key={status.id_stat}
                value={status.id_stat}
                className="text-gray-900"
              >
                {status.nom_stat}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg
              className="h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Avertissements pour les changements sensibles */}
      {warningRules && (
        <div className="space-y-2">
          {Object.values(warningRules).map((rule, index) => {
            if (rule.condition && rule.condition(originalStatus, selectedArticle?.id_stat)) {
              return (
                <div key={index} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="mt-0.5 h-5 w-5 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.348 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-amber-900">
                        {rule.title || 'Attention : Action sensible'}
                      </h5>
                      <p className="mt-1 text-sm text-amber-700">
                        {rule.description || 'Cette action nécessitera une confirmation supplémentaire.'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  ), [selectedArticle, filteredStatusList, warningRules, originalStatus]);

  // Composant de saisie de commentaire
  const CommentInput = useMemo(() => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label htmlFor="content_comm" className="block text-sm font-medium text-gray-900">
          Note d'évaluation ou commentaire
          <span className="ml-1 text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Minimum {minimumCommentLength} caractères</span>
        </div>
      </div>
      
      <div className="relative">
        <div className={`relative rounded-xl border-2 transition-all duration-200 ${
          isFocused 
            ? 'border-[#57A863] ring-4 ring-[#57A863]/10' 
            : commentaire.length < minimumCommentLength 
              ? 'border-red-300 hover:border-red-400' 
              : 'border-gray-300 hover:border-gray-400'
        }`}>
          <textarea
            name="content_comm"
            id="content_comm"
            rows="5"
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Décrivez les raisons de votre décision, les points à améliorer, ou les recommandations pour l'auteur..."
            className="block w-full resize-none rounded-xl border-0 bg-transparent p-4 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0"
            style={{ 
              minHeight: '120px',
              lineHeight: '1.6'
            }}
          />
          
          {/* Indicateur de focus */}
          <div className={`absolute bottom-0 left-0 h-1 rounded-b-xl transition-all duration-300 ${
            isFocused ? 'w-full bg-[#57A863]' : 'w-0 bg-transparent'
          }`} />
        </div>
        
        {/* Compteur de caractères et statut */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {commentaire.length < minimumCommentLength ? (
              <div className="flex items-center space-x-1 text-xs text-red-500">
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">
                  {commentaire.length === 0 ? 'Commentaire requis' : 'Commentaire trop court'}
                </span>
              </div>
            ) : (
              <div className="flex items-center space-x-1 text-xs text-green-600">
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Commentaire valide</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <div className={`text-xs transition-colors duration-200 ${
              commentaire.length < minimumCommentLength ? 'text-red-500' : 'text-gray-500'
            }`}>
              <span className="font-medium">{commentaire.length}</span>
              <span className="mx-1">/</span>
              <span>500 caractères</span>
            </div>
            
            {/* Barre de progression */}
            <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  commentaire.length < minimumCommentLength ? 'bg-red-400' : 'bg-green-400'
                }`}
                style={{ width: `${Math.min((commentaire.length / 500) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Suggestions de commentaires - LIMITÉES À 3 ALÉATOIRES */}
      {commentaire.length === 0 && suggestions.length > 0 && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
          <div className="flex items-start space-x-2">
            <svg className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-medium text-blue-900">Suggestions de commentaires :</h4>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                  3 suggestions aléatoires
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`suggestion-${index}-${suggestion.slice(0, 10)}`} // Clé unique basée sur l'index et le contenu
                    onClick={() => setCommentaire(suggestion)}
                    className="text-xs bg-white border border-blue-300 rounded-md px-2 py-1 text-blue-700 hover:bg-blue-100 transition-colors duration-200 hover:scale-105 transform"
                    title={`Cliquez pour utiliser: "${suggestion}"`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-blue-600">
                💡 Actualisez pour voir d'autres suggestions
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  ), [commentaire, isFocused, minimumCommentLength, suggestions]);

  // Composant des messages d'état
  const StatusMessages = useMemo(() => (
    <>
      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-red-900">Erreur</h5>
              <p className="mt-1 text-sm text-red-700">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {submitSuccess && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-green-900">Succès</h5>
              <p className="mt-1 text-sm text-green-700">
                Le statut a été attribué avec succès !
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  ), [submitError, submitSuccess]);

  // Props à passer au composant enfant
  const statusHandlerProps = {
    // États
    selectedArticle,
    originalStatus,
    statusList: filteredStatusList,
    isSubmitting,
    submitError,
    submitSuccess,
    commentaire,
    isFocused,

    // Fonctions
    initializeStatusAttribution,
    handleSubmitStatus,
    getStatusName,
    setSelectedArticle,
    setCommentaire,
    setSubmitError,
    setSubmitSuccess,

    // Composants
    StatusSelector,
    CommentInput,
    StatusMessages,

    // Validation
    isValid: selectedArticle?.id_stat && 
             selectedArticle.id_stat !== originalStatus && 
             commentaire.length >= minimumCommentLength,
  };

  return children(statusHandlerProps);
};

StatusHandler.propTypes = {
  children: PropTypes.func.isRequired,
  onStatusUpdate: PropTypes.func,
  showConfirmation: PropTypes.bool,
  customStatusFilter: PropTypes.func,
  warningRules: PropTypes.object,
  minimumCommentLength: PropTypes.number,
  suggestionComments: PropTypes.arrayOf(PropTypes.string),
  customModalHeader: PropTypes.node,
};

export default StatusHandler;